import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { fetcher } from '../../../services/fetcher';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { createAccDocument, updateAccDocument, uploadAccDocumentFiles, deleteAccDocumentFile } from '../../../services/mutations';
import { FileAttachmentsFormBlock } from '../../../components/attachments/FileAttachmentsFormBlock';
import { ACC_DOCUMENT_ATTACHMENT_ACCEPT, ACC_DOCUMENT_ATTACHMENT_HINT } from '../../../components/attachments/attachmentAccept';
import { Button, ChevronLeftIcon, DateInput, DocumentTextIcon, Label, Select, TableIcon, Tabs, TabsContent, TabsList, TabsTrigger, TextInput } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { DocumentLinesGrid } from '../shared/DocumentLinesGrid';
import { JournalBalanceBar } from '../shared/JournalBalanceBar';
import { applyDocumentLineFx, isBalanced, localToUsdAmount, perCompanyBalances, sumDebitCredit, sumDebitCreditUsd } from '../shared/balanceUtils';
import { dueDateFromPaymentTerms } from '../shared/paymentTerms';
import { DocumentLineForm, newDocumentLine } from '../shared/documentLineTypes';
import { DocumentFormTemplateBanner } from './DocumentFormTemplateBanner';
import { resolveDocumentFormTemplate, resolveDocumentEditRoute, isPurchaseInvoiceType } from './formTemplates';
import { useAccDocument } from './useData';
import { moduleConfig } from './config';

type Lookup = {
  id: number;
  code: string;
  name: string;
  allowOpenItems?: boolean;
  paymentTermsDays?: number | null;
  currencyIds?: number[];
  companyIds?: number[];
};
type DocumentTypeLookup = { id: number; code: string; name: string; isActive?: boolean; sortOrder?: number };

const todayIso = () => new Date().toISOString().slice(0, 10);

const mapLinesToForm = (lines: NonNullable<ReturnType<typeof useAccDocument>['item']>['lines']): DocumentLineForm[] =>
  (lines ?? []).map((l) => ({
    key: `line-${l.id}`,
    companyId: String(l.companyId),
    accountId: String(l.accountId),
    debitLocal: l.debitLocal != null ? String(l.debitLocal) : '',
    creditLocal: l.creditLocal != null ? String(l.creditLocal) : '',
    currencyId: l.currencyId ? String(l.currencyId) : '',
    rate: l.rate != null ? String(l.rate) : '',
    debitUSD: l.debitUSD != null ? String(l.debitUSD) : '',
    creditUSD: l.creditUSD != null ? String(l.creditUSD) : '',
    narrative: l.narrative ?? '',
    plPeriodFrom: l.plPeriodFrom?.slice(0, 10) ?? '',
    plPeriodTo: l.plPeriodTo?.slice(0, 10) ?? '',
    dn: l.dn ?? '',
    notes: l.notes ?? '',
    linkNumber: l.linkNumber ?? '',
    dueDate: l.dueDate?.slice(0, 10) ?? '',
  }));

const mapLinesToPayload = (lines: DocumentLineForm[]) =>
  lines
    .filter((l) => l.companyId && l.accountId && (l.debitLocal || l.creditLocal || l.debitUSD || l.creditUSD))
    .map((l, idx) => ({
      companyId: Number(l.companyId),
      accountId: Number(l.accountId),
      debitLocal: l.debitLocal ? Number(l.debitLocal) : 0,
      creditLocal: l.creditLocal ? Number(l.creditLocal) : 0,
      currencyId: l.currencyId ? Number(l.currencyId) : undefined,
      rate: l.rate ? Number(l.rate) : undefined,
      debitUSD: localToUsdAmount(l.debitLocal, l.rate),
      creditUSD: localToUsdAmount(l.creditLocal, l.rate),
      narrative: l.narrative || undefined,
      plPeriodFrom: l.plPeriodFrom || undefined,
      plPeriodTo: l.plPeriodTo || undefined,
      dn: l.dn || undefined,
      notes: l.notes || undefined,
      linkNumber: l.linkNumber || undefined,
      dueDate: l.dueDate || undefined,
      lineOrder: idx + 1,
    }));

export const Form: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const forceJournalView = searchParams.get('view') === 'journal';
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const { colors, theme } = useTheme();
  const editId = id && id !== 'new' ? Number(id) : 0;
  const { item, isLoading } = useAccDocument(editId);

  const [documentTypeId, setDocumentTypeId] = useState('');
  const [documentDate, setDocumentDate] = useState(todayIso);
  const [postingDate, setPostingDate] = useState(todayIso);
  const priorPostingDateRef = useRef(todayIso());
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<DocumentLineForm[]>([newDocumentLine(), newDocumentLine()]);
  const [saving, setSaving] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesToDetach, setFilesToDetach] = useState<number[]>([]);

  const { data: companiesRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-companies', fetcher);
  const { data: accountsRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-accounts?postingOnly=true', fetcher);
  const { data: currencies } = useSWR<{ id: number; code: string; name: string }[]>('/currencies', fetcher);
  const { data: documentTypesRaw } = useSWR<DocumentTypeLookup[] | { data: DocumentTypeLookup[] }>('/acc-document-types', fetcher);

  const companies = Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.data ?? [];
  const accounts = Array.isArray(accountsRaw) ? accountsRaw : accountsRaw?.data ?? [];
  const documentTypes = (Array.isArray(documentTypesRaw) ? documentTypesRaw : documentTypesRaw?.data ?? [])
    .filter((t) => t.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));

  const companyOptions = companies.map((c) => ({ value: String(c.id), label: c.name }));
  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: a.name }));
  const currencyOptions = (currencies ?? []).map((c) => ({ value: String(c.id), label: c.name || c.code }));
  const typeOptions = documentTypes.map((t) => ({ value: String(t.id), label: t.name }));
  const selectedDocumentType = useMemo(
    () => documentTypes.find((t) => String(t.id) === documentTypeId) ?? null,
    [documentTypes, documentTypeId],
  );
  const formTemplate = useMemo(
    () => resolveDocumentFormTemplate(selectedDocumentType),
    [selectedDocumentType],
  );
  const companyNames = useMemo(() => {
    const m: Record<number, string> = {};
    for (const c of companies) m[c.id] = `${c.name}`;
    return m;
  }, [companies]);

  const accountMeta = useMemo(() => {
    const m: Record<string, {
      allowOpenItems?: boolean;
      paymentTermsDays?: number | null;
      currencyIds?: number[];
      companyIds?: number[];
    }> = {};
    for (const a of accounts) {
      m[String(a.id)] = {
        allowOpenItems: a.allowOpenItems,
        paymentTermsDays: a.paymentTermsDays,
        currencyIds: a.currencyIds,
        companyIds: a.companyIds,
      };
    }
    return m;
  }, [accounts]);

  const balanceLines = lines
    .filter((l) => l.companyId)
    .map((l) => ({ companyId: Number(l.companyId), debitLocal: l.debitLocal, creditLocal: l.creditLocal }));

  const usdBalanceLines = lines
    .filter((l) => l.companyId)
    .map((l) => ({ debitUSD: l.debitUSD, creditUSD: l.creditUSD }));

  const totals = sumDebitCredit(balanceLines);
  const usdTotals = sumDebitCreditUsd(usdBalanceLines);
  const companyTotals = perCompanyBalances(balanceLines);
  const balanced = isBalanced(balanceLines);

  useEffect(() => {
    if (!documentTypeId && typeOptions.length > 0) {
      const journal = documentTypes.find((t) => t.code === 'JOURNAL');
      setDocumentTypeId(String((journal ?? documentTypes[0]).id));
    }
  }, [documentTypeId, documentTypes, typeOptions.length]);

  useEffect(() => {
    if (!item) return;
    if (!forceJournalView && item.documentType?.code === 'PAYMENT') {
      navigate(resolveDocumentEditRoute(item.documentType, item.id, moduleConfig.module.route.edit(item.id)));
      return;
    }
    if (!forceJournalView && isPurchaseInvoiceType(item.documentType)) {
      navigate(resolveDocumentEditRoute(item.documentType, item.id, moduleConfig.module.route.edit(item.id)));
      return;
    }
    if (item.status !== 'DRAFT' && item.status !== 'TEMP') {
      navigate(moduleConfig.module.route.detail(item.id));
      return;
    }
    setDocumentTypeId(String(item.documentTypeId));
    setDocumentDate(item.documentDate?.slice(0, 10) ?? '');
    setPostingDate(item.postingDate?.slice(0, 10) ?? '');
    priorPostingDateRef.current = item.postingDate?.slice(0, 10) ?? todayIso();
    setDescription(item.description ?? '');
    setLines(
      item.lines?.length
        ? mapLinesToForm(item.lines).map(applyDocumentLineFx)
        : [newDocumentLine(), newDocumentLine()],
    );
  }, [item, navigate, forceJournalView]);

  useEffect(() => {
    if (!postingDate) return;
    const priorPostingDate = priorPostingDateRef.current;
    setLines((prev) =>
      prev.map((line) => {
        if (!line.accountId) return line;
        const meta = accountMeta[line.accountId];
        if (!meta?.allowOpenItems || meta.paymentTermsDays == null) return line;
        const nextDue = dueDateFromPaymentTerms(postingDate, meta.paymentTermsDays);
        if (!nextDue) return line;
        if (!line.dueDate) return { ...line, dueDate: nextDue };
        const priorDue = dueDateFromPaymentTerms(priorPostingDate, meta.paymentTermsDays);
        if (priorDue && line.dueDate === priorDue) return { ...line, dueDate: nextDue };
        return line;
      }),
    );
    priorPostingDateRef.current = postingDate;
  }, [postingDate, accountMeta]);

  const existingAttachments = useMemo(
    () =>
      (item?.attachments ?? [])
        .filter((f) => !filesToDetach.includes(f.id))
        .map((f) => ({ id: f.id, fileName: f.fileName, fileSize: f.fileSize })),
    [item?.attachments, filesToDetach],
  );

  const attachmentCount = existingAttachments.length + newFiles.length;
  const activeLineCount = lines.filter(
    (l) => l.companyId && l.accountId && (l.debitLocal || l.creditLocal || l.debitUSD || l.creditUSD),
  ).length;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!balanced) {
      toast.error('Document must be balanced');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        documentTypeId: documentTypeId ? Number(documentTypeId) : undefined,
        documentDate: documentDate || undefined,
        postingDate: postingDate || undefined,
        description: description || undefined,
        lines: mapLinesToPayload(lines),
      };
      let docId = editId;
      if (editId) {
        await updateAccDocument(editId, payload);
      } else {
        const created = await createAccDocument(payload) as { id: number };
        docId = created.id;
      }
      if (docId) {
        if (newFiles.length) await uploadAccDocumentFiles(docId, newFiles);
        for (const fileId of filesToDetach) {
          await deleteAccDocumentFile(docId, fileId);
        }
      }
      toast.success('Saved');
      navigate(docId ? moduleConfig.module.route.detail(docId) : moduleConfig.module.route.list);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editId && isLoading) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 -mt-1">
      <form
        id="acc-document-form"
        onSubmit={onSubmit}
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div
          className={cn(
            'flex flex-shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-6',
            colors.border,
            colors.headerBg,
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Button type="button" variant="ghost" size="icon" title="Back" aria-label="Back" onClick={goBack}>
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
                theme === 'dark' ? 'bg-slate-700/80 text-slate-200' : 'bg-slate-200/80 text-slate-700',
              )}
            >
              Draft
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span
              className={cn(
                'hidden sm:inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                balanced ? colors.success : colors.warning,
              )}
            >
              {balanced ? 'Balanced' : `Diff ${totals.diff.toFixed(2)}`}
            </span>
            {forceJournalView && editId > 0 && isPurchaseInvoiceType(item?.documentType ?? selectedDocumentType) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/accounting/invoices/${editId}/edit`)}
              >
                Invoice form
              </Button>
            )}
            {forceJournalView && editId > 0 && (item?.documentType?.code === 'PAYMENT' || selectedDocumentType?.code === 'PAYMENT') && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/accounting/payments/${editId}/edit`)}
              >
                Payment form
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={goBack}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!balanced || saving} loading={saving}>
              Save draft
            </Button>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <section className={cn('flex-shrink-0 px-4 sm:px-6 py-4 border-b', colors.border)}>
            <div className="flex flex-row flex-nowrap items-end gap-3 min-w-0 overflow-x-auto pb-0.5">
              <div className="min-w-[12rem] flex-[1_1_16rem]">
                <Label>Description</Label>
                <TextInput
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className="w-36 shrink-0">
                <Label>Type</Label>
                <Select
                  value={documentTypeId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    if (!editId) {
                      const nextType = documentTypes.find((t) => String(t.id) === nextId);
                      const nextTemplate = resolveDocumentFormTemplate(nextType ?? null);
                      if (nextTemplate.entryRoute) {
                        navigate(nextTemplate.entryRoute);
                        return;
                      }
                    }
                    setDocumentTypeId(nextId);
                  }}
                  options={typeOptions}
                  showClearButton={false}
                />
              </div>
              <div className="w-[10.5rem] shrink-0">
                <Label>Doc. date</Label>
                <DateInput value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} showFormatHint={false} />
              </div>
              <div className="w-[10.5rem] shrink-0">
                <Label>Posting date</Label>
                <DateInput
                  value={postingDate}
                  onChange={(e) => setPostingDate(e.target.value)}
                  showFormatHint={false}
                />
              </div>
            </div>
            <div className="mt-3">
              <DocumentFormTemplateBanner
                template={formTemplate}
                documentTypeName={selectedDocumentType?.name}
              />
            </div>
          </section>

          <Tabs defaultValue="lines" className="flex flex-1 flex-col min-h-0">
            <TabsList variant="pill" fit className="mx-4 mt-2 flex-shrink-0 sm:mx-6">
              <TabsTrigger
                value="lines"
                appearance="pill"
                size="sm"
                colorScheme="indigo"
                icon={<TableIcon className="h-3.5 w-3.5" />}
                count={activeLineCount}
              >
                Lines
              </TabsTrigger>
              <TabsTrigger
                value="attachments"
                appearance="pill"
                size="sm"
                colorScheme={newFiles.length > 0 ? 'amber' : 'violet'}
                icon={<DocumentTextIcon className="h-3.5 w-3.5" />}
                count={attachmentCount}
                showPendingDot={newFiles.length > 0}
              >
                Files
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto dashboard-scrollbar min-h-0">
              <TabsContent value="lines" className="mt-0 px-4 pt-1 pb-3 sm:px-6">
                <DocumentLinesGrid
                  lines={lines}
                  companies={companyOptions}
                  accounts={accountOptions}
                  currencies={currencyOptions}
                  accountMeta={accountMeta}
                  postingDate={postingDate}
                  onChange={setLines}
                  formTemplateId={formTemplate.id}
                  defaultShowExtended={formTemplate.defaultExtendedLines}
                  emphasizeLinkPicker={formTemplate.emphasizeLinkPicker}
                />
              </TabsContent>

              <TabsContent value="attachments" className="mt-0 px-4 pt-1 pb-3 sm:px-6">
                <FileAttachmentsFormBlock
                  variant="panel"
                  hint={`${ACC_DOCUMENT_ATTACHMENT_HINT}. Saved when you save the draft.`}
                  accept={ACC_DOCUMENT_ATTACHMENT_ACCEPT}
                  newFiles={newFiles}
                  existingFiles={existingAttachments}
                  onNewFilesChange={setNewFiles}
                  onRemoveExisting={(fileId) => setFilesToDetach((prev) => [...prev, fileId])}
                  getViewFileUrl={
                    editId ? (fileId) => `/acc-documents/${editId}/files/${fileId}` : undefined
                  }
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <div className="flex-shrink-0">
          <JournalBalanceBar
            debitLocal={totals.debit}
            creditLocal={totals.credit}
            debitUSD={usdTotals.debit}
            creditUSD={usdTotals.credit}
            diff={totals.diff}
            balanced={balanced}
            companyTotals={companyTotals}
            companyNames={companyNames}
          />
        </div>
      </form>
    </div>
  );
};
